import { useCallback, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { Loading } from "@/components/Loading";
import useAuth from "@/hooks/useAuth";
import { useToast } from "@/hooks/useToast";
import { ROUTES_PATH } from "@/router";
import {
  GENERIC_ERROR_MESSAGE,
  GENERIC_ERROR_TITLE,
  SERVER_ERROR_MESSAGE,
  SERVER_ERROR_TITLE,
} from "@/constants/messages";

type PartialAuthProps = {
  children: React.ReactNode;
};

export default function PartialAuth({ children }: PartialAuthProps) {
  const {
    session,
    isRecoverySession,
    recoveryPassword,
    loading,
    onUpdatePasswordFlow,
  } = useAuth();
  const { toast } = useToast();

  const handleBeginUpdatePasswordFlow = useCallback(async () => {
    try {
      const { error, sessionError } = await onUpdatePasswordFlow();
      if (error === null && sessionError === null) return;
      toast({
        title: GENERIC_ERROR_TITLE,
        description: GENERIC_ERROR_MESSAGE,
      });
    } catch (error) {
      toast({
        title: SERVER_ERROR_TITLE,
        description: SERVER_ERROR_MESSAGE,
      });
    }
  }, [onUpdatePasswordFlow, toast]);

  useEffect(() => {
    if (!recoveryPassword) {
      return;
    }
    handleBeginUpdatePasswordFlow();
  }, [recoveryPassword, handleBeginUpdatePasswordFlow]);

  if (loading) {
    return <Loading.Absolute />;
  }
  if (!session && !isRecoverySession) {
    return <Navigate to={ROUTES_PATH.LOGIN} replace />;
  }
  if (session && isRecoverySession) {
    return children;
  }
  return null;
}
